import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  alterarStatusFabricanteEstaleiro,
  cadastrarFabricanteEstaleiro,
  editarFabricanteEstaleiro,
  excluirFabricanteEstaleiro,
  listarFabricantesEstaleiro,
} from "./fabricantesEstaleiro.api";
import CriarFabricanteEstaleiroModal from "./popups/CriarFabricanteEstaleiroModal";
import EditarFabricanteEstaleiroModal from "./popups/EditarFabricanteEstaleiroModal";
import ExcluirFabricanteEstaleiroModal from "./popups/ExcluirFabricanteEstaleiroModal";
import AtivarFabricanteEstaleiroModal from "./popups/AtivarFabricanteEstaleiroModal";
import InativarFabricanteEstaleiroModal from "./popups/InativarFabricanteEstaleiroModal";
import { useInfiniteBaseList } from "../shared/useInfiniteBaseList";
import "./FabricantesEstaleiroBase.css";

const sanitizeText = (value, max = 80) =>
  String(value ?? "")
    .replace(/[\u0000-\u001F\u007F]/g, "")
    .replace(/[<>]/g, "")
    .trim()
    .toUpperCase()
    .slice(0, max);

const safeText = (value) => String(value ?? "").trim();
const formatItem = (row) => {
  const joined = safeText(row?.fabricante_modelo);
  if (joined) return joined;
  const fabricante = safeText(row?.fabricante);
  const modelo = safeText(row?.modelo);
  return modelo ? `${fabricante} - ${modelo}` : fabricante;
};

function IconEdit(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 1 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </svg>
  );
}

function IconTrash(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
      <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
    </svg>
  );
}

function ToggleSwitch({ on = false, onToggle, title }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      className={`fbe-toggle ${on ? "on" : "off"}`}
      onClick={onToggle}
      title={title || (on ? "Inativar" : "Ativar")}
    >
      <span className="fbe-toggle-knob" />
    </button>
  );
}

export default function FabricantesEstaleiroBase() {
  const [feedback, setFeedback] = useState("");

  const [filtroTexto, setFiltroTexto] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("todos");

  const [openCriar, setOpenCriar] = useState(false);
  const [openEditar, setOpenEditar] = useState(false);
  const [openExcluir, setOpenExcluir] = useState(false);
  const [openAtivar, setOpenAtivar] = useState(false);
  const [openInativar, setOpenInativar] = useState(false);
  const [rowSelecionada, setRowSelecionada] = useState(null);
  const tableScrollRef = useRef(null);

  const { rows, loading, loadingMore, hasMore, error, reload, loadMore } = useInfiniteBaseList({
    listFn: listarFabricantesEstaleiro,
    queryText: filtroTexto,
    statusFilter: filtroStatus,
    pageSize: 100,
  });

  const stats = useMemo(() => {
    const total = rows.length;
    const ativos = rows.filter((row) => Number(row?.ativo) === 1).length;
    return {
      total,
      ativos,
      inativos: total - ativos,
    };
  }, [rows]);

  const rowsOrdenadas = useMemo(
    () => [...rows].sort((a, b) => formatItem(a).localeCompare(formatItem(b), "pt-BR", { sensitivity: "base" })),
    [rows]
  );

  const handleTableScroll = useCallback(
    (event) => {
      const el = event.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight <= 120) {
        loadMore();
      }
    },
    [loadMore]
  );

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el || loading || loadingMore || !hasMore) return;
    if (el.scrollHeight <= el.clientHeight + 12) {
      loadMore();
    }
  }, [rows.length, loading, loadingMore, hasMore, loadMore]);

  async function criarFabricante({ fabricante, modelo }) {
    const fabricanteValue = sanitizeText(fabricante);
    const modeloValue = sanitizeText(modelo);
    if (!fabricanteValue) throw new Error("Informe o fabricante.");
    await cadastrarFabricanteEstaleiro({ fabricante: fabricanteValue, modelo: modeloValue, ativo: 1 });
    setFeedback("Fabricante cadastrado com sucesso.");
    reload();
  }

  async function editarFabricante({ fabricante, modelo }) {
    if (!rowSelecionada?.id) throw new Error("Registro selecionado invalido.");
    const fabricanteValue = sanitizeText(fabricante);
    const modeloValue = sanitizeText(modelo);
    if (!fabricanteValue) throw new Error("Informe o fabricante.");
    await editarFabricanteEstaleiro(rowSelecionada.id, { fabricante: fabricanteValue, modelo: modeloValue });
    setFeedback("Fabricante atualizado com sucesso.");
    reload();
  }

  async function excluirFabricante() {
    if (!rowSelecionada?.id) throw new Error("Registro selecionado invalido.");
    await excluirFabricanteEstaleiro(rowSelecionada.id);
    setFeedback("Fabricante excluido com sucesso.");
    reload();
  }

  async function ativarFabricante() {
    if (!rowSelecionada?.id) throw new Error("Registro selecionado invalido.");
    await alterarStatusFabricanteEstaleiro(rowSelecionada.id, 1);
    setFeedback("Fabricante ativado com sucesso.");
    reload();
  }

  async function inativarFabricante() {
    if (!rowSelecionada?.id) throw new Error("Registro selecionado invalido.");
    await alterarStatusFabricanteEstaleiro(rowSelecionada.id, 0);
    setFeedback("Fabricante inativado com sucesso.");
    reload();
  }

  return (
    <div className="fbe-base">
      <div className="fbe-cards">
        <div className="fbe-card">
          <span>Total</span>
          <strong>{stats.total}</strong>
        </div>
        <div className="fbe-card">
          <span>Ativos</span>
          <strong>{stats.ativos}</strong>
        </div>
        <div className="fbe-card">
          <span>Inativos</span>
          <strong>{stats.inativos}</strong>
        </div>
      </div>

      <div className="fbe-toolbar">
        <input
          type="text"
          value={filtroTexto}
          onChange={(event) => setFiltroTexto(event.target.value)}
          placeholder="Buscar fabricante ou modelo..."
        />
        <select value={filtroStatus} onChange={(event) => setFiltroStatus(event.target.value)}>
          <option value="todos">Todos</option>
          <option value="ativos">Somente ativos</option>
          <option value="inativos">Somente inativos</option>
        </select>
        <button
          type="button"
          className="fbe-btn fbe-btn--primary"
          onClick={() => {
            setFeedback("");
            setOpenCriar(true);
          }}
        >
          Cadastrar fabricante
        </button>
      </div>

      <div className="fbe-table-wrap">
        <table className="fbe-table fbe-table--head">
          <colgroup>
            <col style={{ width: "30%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "22%" }} />
            <col style={{ width: "26%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Fabricante - Modelo</th>
              <th>Fabricante</th>
              <th>Modelo</th>
              <th>Controles</th>
            </tr>
          </thead>
        </table>

        <div className="fbe-table-scroll" ref={tableScrollRef} onScroll={handleTableScroll}>
          <table className="fbe-table fbe-table--body">
            <colgroup>
              <col style={{ width: "30%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "22%" }} />
              <col style={{ width: "26%" }} />
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={4} className="fbe-empty">Carregando base de fabricantes...</td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={4} className="fbe-empty fbe-empty--error">{error}</td>
                </tr>
              ) : !rowsOrdenadas.length ? (
                <tr>
                  <td colSpan={4} className="fbe-empty">Nenhum fabricante encontrado.</td>
                </tr>
              ) : (
                rowsOrdenadas.map((row) => {
                  const ativo = Number(row?.ativo) === 1;
                  return (
                    <tr key={row.id}>
                      <td>{formatItem(row)}</td>
                      <td>{safeText(row?.fabricante)}</td>
                      <td>{safeText(row?.modelo) || "-"}</td>
                      <td>
                        <div className="fbe-actions">
                          <ToggleSwitch
                            on={ativo}
                            title={ativo ? "Inativar fabricante" : "Ativar fabricante"}
                            onToggle={() => {
                              setRowSelecionada(row);
                              if (ativo) setOpenInativar(true);
                              else setOpenAtivar(true);
                            }}
                          />
                          <button
                            type="button"
                            className="fbe-icon-btn"
                            title="Editar fabricante"
                            onClick={() => {
                              setFeedback("");
                              setRowSelecionada(row);
                              setOpenEditar(true);
                            }}
                          >
                            <IconEdit />
                          </button>
                          <button
                            type="button"
                            className="fbe-icon-btn"
                            title="Excluir fabricante"
                            onClick={() => {
                              setRowSelecionada(row);
                              setOpenExcluir(true);
                            }}
                          >
                            <IconTrash />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
              {!loading && !error && loadingMore ? (
                <tr>
                  <td colSpan={4} className="fbe-empty">Carregando mais...</td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </div>

      {feedback ? <p className="fbe-feedback">{feedback}</p> : null}

      <CriarFabricanteEstaleiroModal
        open={openCriar}
        onClose={() => setOpenCriar(false)}
        onSalvar={criarFabricante}
      />

      <EditarFabricanteEstaleiroModal
        open={openEditar}
        registro={rowSelecionada}
        onClose={() => {
          setOpenEditar(false);
          setRowSelecionada(null);
        }}
        onSalvar={editarFabricante}
      />

      <ExcluirFabricanteEstaleiroModal
        open={openExcluir}
        titulo={formatItem(rowSelecionada)}
        onClose={() => {
          setOpenExcluir(false);
          setRowSelecionada(null);
        }}
        onConfirmar={excluirFabricante}
      />

      <AtivarFabricanteEstaleiroModal
        open={openAtivar}
        titulo={formatItem(rowSelecionada)}
        onClose={() => {
          setOpenAtivar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={ativarFabricante}
      />

      <InativarFabricanteEstaleiroModal
        open={openInativar}
        titulo={formatItem(rowSelecionada)}
        onClose={() => {
          setOpenInativar(false);
          setRowSelecionada(null);
        }}
        onConfirmar={inativarFabricante}
      />
    </div>
  );
}
